// src/pages/NetWorth.js
//
// Chunk 7 — Net Worth restyled for the Merq Vault redesign.
//
// What's new:
//   • Plum/cream/Fraunces chrome to match Log/Plan/Reports/This Month
//   • Tiny 6-month sparkline above the hero number, fed by
//     account_balance_history (no schema changes)
//   • Lucide icons in chrome (emojis stay on user-generated content
//     like account types)
//   • Header layout matches the rest of the redesigned app
//
// What's preserved:
//   • All data queries and CRUD (account + balance history)
//   • Two-mode toggle (Update Balances / My Accounts)
//   • The 16 ACCOUNT_TYPES list (kept emojis here — these ARE
//     user-facing labels, not chrome)
//   • Per-row "as of date" picker
//
import React, { useState, useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, emitDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { useCurrency } from '../lib/CurrencyContext'
import { theme, atoms, firstName } from '../lib/theme'

const ACCOUNT_TYPES = [
  { value: 'checking',     label: 'Checking',           icon: '🏦', asset: true },
  { value: 'savings',      label: 'Savings',            icon: '💰', asset: true },
  { value: 'hysa',         label: 'High-Yield Savings', icon: '📈', asset: true },
  { value: '401k',         label: '401(k)',             icon: '👴', asset: true },
  { value: 'ira',          label: 'IRA',                icon: '📊', asset: true },
  { value: 'brokerage',    label: 'Brokerage',          icon: '📉', asset: true },
  { value: 'crypto',       label: 'Crypto',             icon: '₿',  asset: true },
  { value: 'home',         label: 'Home Value',         icon: '🏠', asset: true },
  { value: 'vehicle',      label: 'Vehicle Value',      icon: '🚗', asset: true },
  { value: 'other_asset',  label: 'Other Asset',        icon: '💎', asset: true },
  { value: 'credit_card',  label: 'Credit Card',        icon: '💳', asset: false },
  { value: 'student_loan', label: 'Student Loan',       icon: '🎓', asset: false },
  { value: 'auto_loan',    label: 'Auto Loan',          icon: '🚗', asset: false },
  { value: 'mortgage',     label: 'Mortgage',           icon: '🏠', asset: false },
  { value: 'personal_loan',label: 'Personal Loan',      icon: '💵', asset: false },
  { value: 'other_debt',   label: 'Other Debt',         icon: '📋', asset: false },
]

const getType = (value) => ACCOUNT_TYPES.find(t => t.value === value) || { label: value, icon: '💰' }

// ─── Sparkline helper ───────────────────────────────────────
// Renders a small SVG line chart from N points.
// `points` is [{x, y}], y is net worth at that month.
function NetWorthSparkline({ points, width = 320, height = 56 }) {
  if (!points || points.length < 2) return null
  const ys = points.map(p => p.y)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const range = (maxY - minY) || 1
  const padX = 4
  const padY = 8
  const innerW = width - padX * 2
  const innerH = height - padY * 2

  // Project data points to canvas coords
  const proj = points.map((p, i) => ({
    x: padX + (i / (points.length - 1)) * innerW,
    y: padY + (1 - (p.y - minY) / range) * innerH,
    raw: p,
  }))

  // Build smooth path
  const d = proj.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')

  // Build gradient fill area underneath
  const fillD = `${d} L ${proj[proj.length - 1].x.toFixed(1)} ${height} L ${proj[0].x.toFixed(1)} ${height} Z`

  // Direction (up/flat/down) for color
  const first = proj[0].raw.y
  const last = proj[proj.length - 1].raw.y
  const trend = last > first ? 'up' : last < first ? 'down' : 'flat'
  const stroke = trend === 'up'
    ? theme.colors.greenMint
    : trend === 'down'
      ? '#FCA5A5'
      : 'rgba(250, 247, 242, 0.5)'

  const lastPoint = proj[proj.length - 1]

  return (
    <svg
      width={width} height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="nw-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillD} fill="url(#nw-spark-fill)" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPoint.x} cy={lastPoint.y} r="3" fill={stroke} />
      <circle cx={lastPoint.x} cy={lastPoint.y} r="6"
        fill="none" stroke={stroke} strokeWidth="1.5" opacity="0.4" />
    </svg>
  )
}

export default function NetWorth() {
  const { formatCurrency } = useCurrency()
  const { partnerId, partnerProfile } = usePartner()
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile

 const [accounts, setAccounts] = useState([])
  const [debts, setDebts] = useState([])
  const [history, setHistory] = useState([])
  const [debtHistory, setDebtHistory] = useState([]) // Chunk 9b — per-timestamp debt balances
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [mode, setMode] = useState('update') // 'update' | 'accounts'

  // Account setup form
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [name, setName] = useState('')
  const [type, setType] = useState('checking')
  const [institution, setInstitution] = useState('')
  const [isAsset, setIsAsset] = useState(true)
  const [savingForm, setSavingForm] = useState(false)

  // Balance update — local edits keyed by account id
  const [balanceEdits, setBalanceEdits] = useState({})
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll() }, [partnerId])
  // Refetch when accounts or debts change anywhere in the app
  useDataEvent(
    [
      EVENTS.ACCOUNT_SAVED,
      EVENTS.ACCOUNT_UPDATED,
      EVENTS.ACCOUNT_DELETED,
      EVENTS.ACCOUNT_BALANCE_UPDATED,
      EVENTS.DEBT_SAVED,
      EVENTS.DEBT_UPDATED,
      EVENTS.DEBT_DELETED,
    ],
    () => fetchAll()
  )

  const fetchAll = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    const [acctRes, histRes, debtsRes, debtHistRes] = await Promise.all([
      supabase.from('accounts')
        .select('*, profiles!accounts_user_id_fkey(full_name)')
        .in('user_id', userIds)
        .order('is_asset', { ascending: false })
        .order('sort_order'),
      supabase.from('account_balance_history')
        .select('account_id, balance, recorded_at')
        .in('user_id', userIds)
        .order('recorded_at', { ascending: true })
        .limit(2000),
      supabase.from('debts')
        .select('*, profiles!debts_user_id_fkey(full_name)')
        .in('user_id', userIds)
        .order('created_at'),
      // Per-timestamp debt balances (Chunk 9b). RLS scopes this to the
      // household's debts, so no explicit debt_id filter is needed.
      supabase.from('debt_balance_history')
        .select('debt_id, balance, recorded_at')
        .order('recorded_at', { ascending: true })
        .limit(4000),
    ])

    if (acctRes.data) {
      setAccounts(acctRes.data)
      const edits = {}
      acctRes.data.forEach(a => { edits[a.id] = a.current_balance?.toString() || '' })

      // Also seed edits for debts
      ;(debtsRes.data || []).forEach(d => {
        edits[`debt:${d.id}`] = d.current_balance?.toString() || ''
      })
      setBalanceEdits(edits)

      // Determine mode based on having ANY accounts or debts
      if (acctRes.data.length === 0 && (debtsRes.data || []).length === 0) {
        setMode('accounts')
      } else {
        setMode('update')
      }
    }
    setDebts(debtsRes.data || [])
    setHistory(histRes.data || [])
    setDebtHistory(debtHistRes.data || [])
    setLoading(false)
  }

  // ── UPDATE BALANCES ────────────────────────────────────────
  const handleSaveAllBalances = async () => {
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const recordedAt = new Date(asOfDate + 'T12:00:00').toISOString()

    // 1. Update accounts (and write to balance history)
    const accountUpdates = accounts.map(async (account) => {
      const newBalance = parseFloat(balanceEdits[account.id])
      if (isNaN(newBalance)) return
      if (newBalance === parseFloat(account.current_balance)) return
      await supabase.from('accounts').update({
        current_balance: newBalance,
        last_updated: recordedAt,
      }).eq('id', account.id)
      await supabase.from('account_balance_history').insert({
        account_id: account.id,
        user_id: user.id,
        balance: newBalance,
        recorded_at: recordedAt,
      })
      emitDataEvent(EVENTS.ACCOUNT_BALANCE_UPDATED)
    })

    // 2. Update debts (no separate history table — debts.updated_at moves)
    const debtUpdates = debts.map(async (debt) => {
      const newBalance = parseFloat(balanceEdits[`debt:${debt.id}`])
      if (isNaN(newBalance)) return
      if (newBalance === parseFloat(debt.current_balance)) return
      await supabase.from('debts').update({
        current_balance: newBalance,
        updated_at: recordedAt,
      }).eq('id', debt.id)
      emitDataEvent(EVENTS.DEBT_UPDATED)
    })

    await Promise.all([...accountUpdates, ...debtUpdates])

    await fetchAll()
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  // ── ACCOUNT SETUP ──────────────────────────────────────────
  const resetForm = () => {
    setName(''); setType('checking'); setInstitution(''); setIsAsset(true)
    setEditingAccount(null); setShowAddForm(false)
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setName(account.name)
    setType(account.type)
    setInstitution(account.institution || '')
    setIsAsset(account.is_asset)
    setShowAddForm(true)
    window.scrollTo(0, 0)
  }

  const handleSaveAccount = async () => {
    if (!name) return
    setSavingForm(true)
    const { data: { user } } = await supabase.auth.getUser()
    const accountData = { name, type, institution: institution || null, is_asset: isAsset }

    if (editingAccount) {
      await supabase.from('accounts').update(accountData).eq('id', editingAccount.id)
      emitDataEvent(EVENTS.ACCOUNT_UPDATED)
    } else {
      const { data: newAcct } = await supabase.from('accounts')
        .insert({ ...accountData, user_id: user.id, current_balance: 0 })
        .select().single()
      if (newAcct) {
        await supabase.from('account_balance_history').insert({
          account_id: newAcct.id, user_id: user.id, balance: 0,
          recorded_at: new Date().toISOString(),
        })
        emitDataEvent(EVENTS.ACCOUNT_SAVED)
      }
    }
    resetForm()
    await fetchAll()
    setSavingForm(false)
    if (!editingAccount) setMode('update')
  }

  const handleDelete = async (account) => {
    if (!window.confirm(`Delete "${account.name}"? This will also remove its balance history.`)) return
    await supabase.from('accounts').delete().eq('id', account.id)
    emitDataEvent(EVENTS.ACCOUNT_DELETED)
    fetchAll()
  }
  // ── COMPUTED ──────────────────────────────────────────────
  const assets = accounts.filter(a => a.is_asset)

  // Liabilities = account-based (is_asset=false) + synthetic rows from debts table.
  // Each debt is mapped to look like an account row so the existing UI
  // can render it without changes. The id is prefixed with "debt:" so
  // we can detect it in handleSaveAllBalances and route the update to
  // the debts table instead.
  const accountLiabilities = accounts.filter(a => !a.is_asset)
  const debtLiabilities = debts.map(d => ({
    id: `debt:${d.id}`,
    _debtId: d.id,
    _isDebt: true,
    name: d.name,
    type: d.type || 'debt',
    institution: null,
    is_asset: false,
    current_balance: d.current_balance,
    last_updated: d.updated_at,
    profiles: d.profiles,
  }))
  const liabilities = [...accountLiabilities, ...debtLiabilities]

  const totalAssets = assets.reduce((s, a) => s + parseFloat(a.current_balance || 0), 0)
  const totalLiabilities = liabilities.reduce((s, a) => s + parseFloat(a.current_balance || 0), 0)
  const netWorth = totalAssets - totalLiabilities

  // Build sparkline data: last ~6 months of net worth.
  // Net worth at any point in time = sum of latest balance per account up to
  // that date (account liabilities subtracted), minus each debt's balance AS OF
  // that date, resolved from debt_balance_history (Chunk 9b, triage #6). This
  // makes debt paydown show as an upward trend: accounts flat + debt decreasing
  // → net worth rises.
  const sparklineData = useMemo(() => {
    if (accounts.length === 0 || history.length === 0) return null

    // Per-debt balance history, sorted ascending, for point-in-time lookup.
    const debtHistByDebt = {}
    debtHistory.forEach(h => {
      (debtHistByDebt[h.debt_id] = debtHistByDebt[h.debt_id] || []).push(h)
    })
    Object.values(debtHistByDebt).forEach(arr =>
      arr.sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime())
    )

    // Total debt owed as of `cutoffMs`: for each debt, the latest history row at
    // or before that time; if the point predates the debt's first record, use
    // its earliest known balance; if a debt has no history at all, fall back to
    // its current balance.
    const debtTotalAt = (cutoffMs) => {
      let total = 0
      for (const d of debts) {
        const rows = debtHistByDebt[d.id]
        let bal = null
        if (rows && rows.length) {
          for (const r of rows) {
            if (new Date(r.recorded_at).getTime() <= cutoffMs) bal = parseFloat(r.balance || 0)
            else break
          }
          if (bal === null) bal = parseFloat(rows[0].balance || 0) // before first record
        }
        total += bal !== null ? bal : parseFloat(d.current_balance || 0)
      }
      return total
    }

    // Map accountId -> is_asset flag
    const assetMap = {}
    accounts.forEach(a => { assetMap[a.id] = a.is_asset })

    // Get the trailing 6 months including current
    const today = new Date()
    const months = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
      months.push({ year: d.getFullYear(), month: d.getMonth(), label: d })
    }

    // For each month-end, compute net worth using the latest balance
    // recorded for each account at-or-before that date.
    const points = months.map((m) => {
      // Last day of this month
      const cutoff = new Date(m.year, m.month + 1, 0, 23, 59, 59)
      const cutoffMs = cutoff.getTime()
      const latestPerAccount = {}
      for (const h of history) {
        const t = new Date(h.recorded_at).getTime()
        if (t > cutoffMs) continue
        const prev = latestPerAccount[h.account_id]
        if (!prev || new Date(prev.recorded_at).getTime() < t) {
          latestPerAccount[h.account_id] = h
        }
      }
      let total = 0
      for (const accountId in latestPerAccount) {
        const balance = parseFloat(latestPerAccount[accountId].balance || 0)
        total += assetMap[accountId] ? balance : -balance
      }
      // Subtract debt owed as of this month-end (per-timestamp — see above).
      return { x: m.label.getTime(), y: total - debtTotalAt(cutoffMs), label: m.label }
    })

    // Need at least 2 distinct y-values to be a meaningful line
    const distinct = new Set(points.map(p => Math.round(p.y * 100)))
    if (distinct.size < 2) return null

    return points
  }, [accounts, history, debts, debtHistory])

  const trend = useMemo(() => {
    if (!sparklineData || sparklineData.length < 2) return null
    const first = sparklineData[0].y
    const last = sparklineData[sparklineData.length - 1].y
    const delta = last - first
    return { delta, dir: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' }
  }, [sparklineData])

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  if (loading) {
    return (
      <div style={styles.loading}>
        <span>Loading net worth…</span>
      </div>
    )
  }

  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* Header */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.pageTitle}>Net worth</h1>
          <div style={atoms.pageMeta}>
            <span>Manual balance tracking · 5 minutes a month</span>
            {sharingFirst && (
              <>
                <span style={atoms.pageMetaDot} />
                <span style={atoms.sharingPill}>
                  Sharing with {sharingFirst}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero card */}
      <div style={styles.hero}>
        <div style={styles.heroKicker}>
          {partnerProfile ? 'Combined net worth' : 'Net worth'}
        </div>
        <div style={{
          ...styles.heroValue,
          color: netWorth >= 0 ? theme.colors.greenMint : '#FCA5A5',
        }}>
          {formatCurrency(netWorth)}
        </div>

        {/* Sparkline */}
        {sparklineData && (
          <div style={styles.sparkWrap}>
            <NetWorthSparkline points={sparklineData} width={isMobile ? 280 : 360} height={56} />
            {trend && trend.dir !== 'flat' && (
              <div style={styles.sparkDelta}>
                <span>
                  {trend.delta >= 0 ? '+' : ''}
                  {formatCurrency(trend.delta)} over 6 months
                </span>
              </div>
            )}
          </div>
        )}

        <div style={styles.heroSplit}>
          <div style={styles.heroSplitItem}>
            <div style={styles.heroSplitLabel}>Assets</div>
            <div style={{ ...styles.heroSplitValue, color: theme.colors.greenMint }}>
              {formatCurrency(totalAssets)}
            </div>
          </div>
          <div style={styles.heroSplitDivider} />
          <div style={styles.heroSplitItem}>
            <div style={styles.heroSplitLabel}>Liabilities</div>
            <div style={{ ...styles.heroSplitValue, color: '#FCA5A5' }}>
              {formatCurrency(totalLiabilities)}
            </div>
          </div>
        </div>
      </div>

      {/* Mode toggle */}
      <div style={styles.modeBar}>
        <button
          type="button" className="btn-compact"
          style={{
            ...styles.modeBtn,
            ...(mode === 'update' ? styles.modeBtnActive : {}),
          }}
          onClick={() => { setMode('update'); setShowAddForm(false) }}
        >
          Update balances
        </button>
        <button
          type="button" className="btn-compact"
          style={{
            ...styles.modeBtn,
            ...(mode === 'accounts' ? styles.modeBtnActive : {}),
          }}
          onClick={() => { setMode('accounts'); setShowAddForm(false) }}
        >
          My accounts
        </button>
      </div>

      {/* ══ UPDATE BALANCES MODE ══════════════════════════════ */}
      {mode === 'update' && (
        <div>
          {accounts.length === 0 ? (
            <EmptyState
              title="No accounts set up yet"
              text={<>First, add your accounts under <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>My accounts</strong>. Then come back here each month to update your balances.</>}
              cta="Set up accounts"
              onCta={() => setMode('accounts')}
            />
          ) : (
            <>
              <div style={isMobile ? styles.updateHeaderMobile : styles.updateHeader}>
                <div style={styles.updateHeaderText}>
                  <div style={styles.updateTitle}>Update your balances</div>
                  <div style={styles.updateSub}>
                    Enter your current balance for each account. Merq Vault doesn't connect to
                    your bank — total privacy. Check your bank app, update once a month.
                  </div>
                </div>
                <div style={styles.asOfWrap}>
                  <label style={styles.asOfLabel} htmlFor="nw-as-of">As of date</label>
                  <input
                    id="nw-as-of"
                    style={styles.asOfInput}
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Assets */}
              {assets.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Assets</div>
                  <div style={styles.rowGroup}>
                    {assets.map(account => (
                      <UpdateRow
                        key={account.id}
                        account={account}
                        partnerProfile={partnerProfile}
                        value={balanceEdits[account.id] ?? ''}
                        onChange={(v) => setBalanceEdits(prev => ({ ...prev, [account.id]: v }))}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Liabilities */}
              {liabilities.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Liabilities</div>
                  <div style={styles.rowGroup}>
                    {liabilities.map(account => (
                      <UpdateRow
                        key={account.id}
                        account={account}
                        partnerProfile={partnerProfile}
                        value={balanceEdits[account.id] ?? ''}
                        onChange={(v) => setBalanceEdits(prev => ({ ...prev, [account.id]: v }))}
                        formatDate={formatDate}
                        liability
                      />
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.saveBar}>
                {saved && (
                  <span style={styles.savedMsg}>
                    Balances updated
                  </span>
                )}
                <button
                  type="button"
                  style={{
                    ...styles.saveAllBtn,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                  onClick={handleSaveAllBalances}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save all balances'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ MY ACCOUNTS MODE ══════════════════════════════════ */}
      {mode === 'accounts' && (
        <div>
          <div style={isMobile ? styles.updateHeaderMobile : styles.updateHeader}>
            <div style={styles.updateHeaderText}>
              <div style={styles.updateTitle}>Your accounts</div>
              <div style={styles.updateSub}>
                Add accounts once — checking, savings, investments, home, loans, credit cards.
                Then update balances monthly to keep your net worth accurate.
              </div>
            </div>
            <button
              type="button"
              style={styles.addBtn}
              onClick={() => { resetForm(); setShowAddForm(!showAddForm) }}
            >
              {showAddForm && !editingAccount ? 'Cancel' : 'Add account'}
            </button>
          </div>

          {/* Add / edit form */}
          {showAddForm && (
            <div style={styles.form}>
              <div style={styles.formTitle}>
                {editingAccount ? 'Edit account' : 'Add account'}
              </div>
              <div style={styles.assetToggle}>
                <button
                  type="button" className="btn-compact"
                  style={{
                    ...styles.assetBtn,
                    ...(isAsset ? styles.assetBtnActive : {}),
                  }}
                  onClick={() => { setIsAsset(true); setType('checking') }}
                >
                  Asset
                </button>
                <button
                  type="button" className="btn-compact"
                  style={{
                    ...styles.assetBtn,
                    ...(!isAsset ? styles.liabilityBtnActive : {}),
                  }}
                  onClick={() => { setIsAsset(false); setType('credit_card') }}
                >
                  Liability
                </button>
              </div>
              <div style={styles.formGrid}>
                <FormField label="Account name" required>
                  <input
                    style={styles.input} type="text"
                    placeholder="e.g. Chase Checking"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </FormField>
                <FormField label="Type">
                  <select
                    style={styles.input}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {ACCOUNT_TYPES.filter(t => t.asset === isAsset).map(t => (
                      <option key={t.value} value={t.value}>
                        {t.icon} {t.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Institution (optional)">
                  <input
                    style={styles.input} type="text"
                    placeholder="e.g. Chase, Vanguard"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                  />
                </FormField>
              </div>
              <div style={styles.setupNote}>
                <span>After adding, you'll be taken to Update balances to enter the current amount.</span>
              </div>
              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={resetForm}
                >
                  Cancel
                </button>
                {editingAccount && (
                  <button
                    type="button"
                    style={styles.deleteBtn}
                    onClick={() => handleDelete(editingAccount)}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    ...styles.saveBtn,
                    flex: 1,
                    opacity: !name ? 0.5 : 1,
                    cursor: !name ? 'not-allowed' : 'pointer',
                  }}
                  onClick={handleSaveAccount}
                  disabled={savingForm || !name}
                >
                  {savingForm ? 'Saving…' : editingAccount ? 'Save changes' : 'Add account'}
                </button>
              </div>
            </div>
          )}

          {accounts.length === 0 && !showAddForm ? (
            <EmptyState
              title="No accounts yet"
              text="Add your checking, savings, investments, home value, loans, and credit cards to build your complete financial picture."
              cta="Add your first account"
              onCta={() => setShowAddForm(true)}
            />
          ) : (
            <>
              {assets.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>
                    Assets · {formatCurrency(totalAssets)}
                  </div>
                  <div style={styles.rowGroup}>
                    {assets.map(account => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        partnerProfile={partnerProfile}
                        onEdit={() => handleEdit(account)}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {liabilities.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>
                    Liabilities · {formatCurrency(totalLiabilities)}
                  </div>
                  <div style={styles.rowGroup}>
                    {liabilities.map(account => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        partnerProfile={partnerProfile}
                        onEdit={() => handleEdit(account)}
                        formatDate={formatDate}
                        liability
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────

function UpdateRow({ account, partnerProfile, value, onChange, formatDate, liability }) {
  const t = getType(account.type)
  const personName = account.profiles?.full_name ? firstName(account.profiles.full_name) : null
  return (
    <div style={styles.row}>
      <div style={styles.rowLeft}>
        <div style={styles.accountIcon}>{t.icon}</div>
        <div style={styles.rowText}>
          <div style={styles.rowName}>{account.name}</div>
          <div style={styles.rowMeta}>
            {t.label}
            {account.institution && ` · ${account.institution}`}
            {account.last_updated && ` · last updated ${formatDate(account.last_updated)}`}
            {partnerProfile && personName && (
              <span style={styles.rowWho}> · {personName}</span>
            )}
          </div>
        </div>
      </div>
      <div style={styles.rowRight}>
        <span style={styles.dollarSign}>$</span>
        <input
          style={{
            ...styles.balanceInput,
            color: liability ? theme.colors.rose : theme.colors.plum,
          }}
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          aria-label={`Balance for ${account.name}`}
        />
      </div>
    </div>
  )
}

function AccountCard({ account, partnerProfile, onEdit, formatDate, liability }) {
  const { formatCurrency } = useCurrency()
  const t = getType(account.type)
  const personName = account.profiles?.full_name ? firstName(account.profiles.full_name) : null
  return (
    <div style={styles.row}>
      <div style={styles.rowLeft}>
        <div style={styles.accountIcon}>{t.icon}</div>
        <div style={styles.rowText}>
          <div style={styles.rowName}>{account.name}</div>
          <div style={styles.rowMeta}>
            {t.label}
            {account.institution && ` · ${account.institution}`}
            {account.last_updated
              ? ` · updated ${formatDate(account.last_updated)}`
              : ' · no balance recorded yet'}
            {partnerProfile && personName && (
              <span style={styles.rowWho}> · {personName}</span>
            )}
          </div>
        </div>
      </div>
      <div style={styles.cardRight}>
        <div style={{
          ...styles.cardBalance,
          color: liability ? theme.colors.rose : theme.colors.plum,
        }}>
          {formatCurrency(account.current_balance)}
        </div>
        <button
          type="button" className="btn-compact"
          style={styles.editBtn}
          onClick={onEdit}
          aria-label={`Edit ${account.name}`}
        >
          Edit
        </button>
      </div>
    </div>
  )
}

function FormField({ label, required, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>
        {label}{required && <span style={styles.fieldRequired}> *</span>}
      </span>
      {children}
    </label>
  )
}

function EmptyState({ icon, title, text, cta, ctaIcon, onCta }) {
  return (
    <div style={styles.empty}>
      {icon && <div style={styles.emptyIcon}>{icon}</div>}
      <h2 style={styles.emptyTitle}>{title}</h2>
      <p style={styles.emptyText}>{text}</p>
      {cta && (
        <button type="button" style={styles.emptyCta} onClick={onCta}>
          {ctaIcon}
          {cta}
        </button>
      )}
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  page: {
    maxWidth: '900px', margin: '0 auto', width: '100%',
    padding: '24px 32px 120px',
    fontFamily: f.body, color: c.plum,
  },
  pageMobile: {
    width: '100%', padding: '0 16px 80px',
    fontFamily: f.body, color: c.plum,
  },
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '10px', padding: '60px', color: c.inkMuted, fontSize: '14px',
  },

  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '20px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    padding: '14px 0 14px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },

  // Hero
  hero: {
    background: c.plum,
    borderRadius: '20px',
    padding: '28px',
    marginBottom: '20px',
    color: c.cream,
    boxShadow: '0 14px 40px rgba(30, 16, 64, 0.18)',
  },
  heroKicker: {
    fontSize: '11.5px', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'rgba(250, 247, 242, 0.55)',
    marginBottom: '10px',
  },
  heroValue: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '52px', letterSpacing: '-0.03em',
    lineHeight: 1.05,
    marginBottom: '18px',
  },
  sparkWrap: {
    marginBottom: '20px',
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  sparkDelta: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '12px', fontWeight: 600,
    color: 'rgba(250, 247, 242, 0.7)',
  },
  heroSplit: {
    display: 'flex', alignItems: 'center', gap: '20px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(250, 247, 242, 0.1)',
  },
  heroSplitItem: { flex: 1 },
  heroSplitLabel: {
    fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'rgba(250, 247, 242, 0.55)',
    marginBottom: '4px',
  },
  heroSplitValue: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '20px', letterSpacing: '-0.02em',
  },
  heroSplitDivider: {
    width: '1px', height: '36px',
    background: 'rgba(250, 247, 242, 0.1)',
  },

  // Mode toggle
  modeBar: {
    display: 'flex', gap: '4px',
    background: c.cream3, borderRadius: '12px',
    padding: '4px', marginBottom: '20px',
  },
  modeBtn: {
    flex: 1,
    padding: '10px 14px',
    background: 'transparent', border: 'none',
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    color: c.inkMuted, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    transition: 'all 120ms ease',
  },
  modeBtnActive: {
    background: c.white, color: c.plum,
    boxShadow: '0 1px 3px rgba(30, 16, 64, 0.1)',
  },

  // Update header / accounts header
  updateHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '18px', gap: '20px',
  },
  updateHeaderMobile: {
    display: 'flex', flexDirection: 'column', gap: '14px',
    marginBottom: '18px',
  },
  updateHeaderText: { flex: 1, minWidth: 0 },
  updateTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '4px',
  },
  updateSub: {
    fontSize: '13px', color: c.inkMuted, lineHeight: 1.55,
  },
  asOfWrap: { flexShrink: 0 },
  asOfLabel: {
    display: 'block', fontSize: '11px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase', marginBottom: '6px',
  },
  asOfInput: {
    padding: '9px 12px',
    background: c.white, border: `1px solid ${c.lineStrong}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px',
    color: c.plum, outline: 'none',
  },

  // Section
  section: { marginBottom: '22px' },
  sectionTitle: {
    fontSize: '11px', fontWeight: 700,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: '10px', padding: '0 4px',
  },

  rowGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },

  // Update / account row (shared base)
  row: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '12px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '14px 16px',
    fontFamily: f.body,
  },
  rowLeft: {
    display: 'flex', alignItems: 'center', gap: '14px',
    flex: 1, minWidth: 0,
  },
  accountIcon: {
    fontSize: '22px', width: '36px', textAlign: 'center',
    flexShrink: 0,
  },
  rowText: { flex: 1, minWidth: 0 },
  rowName: {
    fontSize: '14.5px', fontWeight: 600, color: c.plum,
    marginBottom: '2px',
  },
  rowMeta: {
    fontSize: '12px', color: c.inkMuted,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  rowWho: { color: c.plumSoft, fontWeight: 600 },
  rowRight: {
    display: 'flex', alignItems: 'center', gap: '4px',
    flexShrink: 0,
  },
  dollarSign: {
    fontSize: '17px', color: c.inkQuiet, fontWeight: 300,
  },
  balanceInput: {
    width: '130px',
    padding: '10px 12px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '8px',
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    letterSpacing: '-0.015em',
    outline: 'none',
    textAlign: 'right',
  },
  cardRight: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: '6px',
    flexShrink: 0,
  },
  cardBalance: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '17px', letterSpacing: '-0.015em',
  },
  editBtn: {
    padding: '4px 10px',
    background: c.cream2, color: c.plumSoft,
    border: `1px solid ${c.line}`,
    borderRadius: '6px',
    fontFamily: f.body, fontSize: '11.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },

  // Save bar
  saveBar: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'flex-end', gap: '14px',
    marginTop: '12px', paddingTop: '16px',
    borderTop: `1px solid ${c.line}`,
  },
  savedMsg: {
    fontSize: '13px', color: c.green, fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  saveAllBtn: {
    padding: '12px 24px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },

  // Add button (top-right)
  addBtn: {
    background: c.plum, color: c.cream,
    padding: '10px 16px',
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    flexShrink: 0,
  },

  // Form
  form: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '22px',
    marginBottom: '18px',
    boxShadow: theme.shadows.card,
  },
  formTitle: {
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    marginBottom: '16px',
  },
  assetToggle: {
    display: 'flex', gap: '8px', marginBottom: '16px',
  },
  assetBtn: {
    flex: 1, padding: '11px',
    background: c.cream2, color: c.inkMuted,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  assetBtnActive: {
    background: c.greenSoft, color: c.green,
    borderColor: 'rgba(22, 163, 74, 0.3)',
  },
  liabilityBtnActive: {
    background: c.roseSoft, color: c.rose,
    borderColor: 'rgba(190, 24, 93, 0.3)',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '12px',
  },
  field: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    marginBottom: '4px',
  },
  fieldLabel: {
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  fieldRequired: { color: c.rose },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },
  setupNote: {
    fontSize: '12.5px', color: c.plumSoft,
    background: c.greenSoft,
    padding: '10px 14px', borderRadius: '8px',
    marginBottom: '14px',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  formActions: { display: 'flex', gap: '8px' },
  cancelBtn: {
    padding: '11px 16px',
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
  },
  deleteBtn: {
    padding: '11px 14px',
    background: c.roseSoft, color: c.rose,
    border: `1px solid rgba(190, 24, 93, 0.2)`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  saveBtn: {
    padding: '11px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '8px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', gap: '6px',
  },

  // Empty state
  empty: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '16px',
    padding: '40px 28px',
    textAlign: 'center',
    maxWidth: '480px', margin: '20px auto',
    boxShadow: theme.shadows.card,
  },
  emptyIcon: {
    width: '56px', height: '56px', borderRadius: '16px',
    background: c.cream2, color: c.plumSoft,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 16px',
    border: `1px solid ${c.line}`,
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    margin: '0 0 8px',
  },
  emptyText: {
    fontSize: '14px', color: c.inkMuted, lineHeight: 1.55,
    margin: '0 0 18px',
  },
  emptyCta: {
    background: c.plum, color: c.cream,
    border: 'none', padding: '11px 20px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
}
